import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2, Play, RotateCcw } from 'lucide-react';
import { simulateTargeting, type SimulatorProfile, type SimulatorResult, type SimulatorSurveyResult, type SimulatorQualResult } from '../../lib/adminApi';

const DEFAULT_PROFILE: SimulatorProfile = {
  age: 35,
  gender: 'female',
  zip_code: '01810',
};

const HHI_OPTIONS = [
  { value: 15000, label: 'Under $25,000' },
  { value: 37500, label: '$25,000–$49,999' },
  { value: 62500, label: '$50,000–$74,999' },
  { value: 87500, label: '$75,000–$99,999' },
  { value: 125000, label: '$100,000–$149,999' },
  { value: 175000, label: '$150,000+' },
];

const EDUCATION_OPTIONS = ['less_than_high_school', 'high_school', 'some_college', 'associates', 'bachelors', 'masters', 'doctorate', 'prefer_not_to_say'];
const EMPLOYMENT_OPTIONS = ['full_time', 'part_time', 'self_employed', 'unemployed', 'retired', 'student', 'homemaker'];
const RACE_OPTIONS = ['white', 'black', 'asian', 'hispanic', 'native_american', 'native_hawaiian', 'mixed', 'other', 'prefer_not'];
const LANGUAGE_OPTIONS = ['english', 'spanish', 'french', 'chinese', 'other'];
const PET_OPTIONS = ['dog', 'cat', 'dog_and_cat', 'other_pets', 'no_pets'];
const HOME_OPTIONS = ['own', 'rent', 'other'];
const AUTO_MAKE_OPTIONS = ['ford', 'chevrolet', 'toyota', 'honda', 'jeep', 'nissan', 'ram', 'gmc', 'hyundai', 'kia', 'subaru', 'volkswagen', 'bmw', 'mercedes', 'other', 'no_vehicle'];
const POLITICAL_OPTIONS = ['democrat', 'republican', 'independent', 'other', 'prefer_not_to_say'];
const INDUSTRY_OPTIONS = ['technology', 'healthcare', 'finance', 'education', 'retail', 'manufacturing', 'government', 'nonprofit', 'construction', 'hospitality', 'other', 'not_employed'];
const AILMENT_OPTIONS = ['diabetes', 'heart_disease', 'high_blood_pressure', 'asthma', 'arthritis', 'depression_anxiety', 'cancer'];
const INSURANCE_OPTIONS = ['health_insurance', 'auto_insurance', 'life_insurance', 'homeowners_insurance', 'renters_insurance'];
const SOCIAL_OPTIONS = ['facebook', 'instagram', 'tiktok', 'twitter_x', 'youtube', 'snapchat', 'linkedin'];

function humanLabel(v: string): string {
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = 'w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary';

function MultiCheck({ value, options, onChange }: { value: string[]; options: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {options.map((opt) => {
        const checked = value.includes(opt);
        return (
          <label key={opt} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onChange(checked ? value.filter((v) => v !== opt) : [...value, opt])}
              className="rounded border-slate-600 bg-slate-900"
            />
            {humanLabel(opt)}
          </label>
        );
      })}
    </div>
  );
}

function QualRow({ q, color }: { q: SimulatorQualResult; color: 'green' | 'red' | 'amber' }) {
  const cls = color === 'green'
    ? 'text-emerald-300'
    : color === 'red'
      ? 'text-red-300'
      : 'text-amber-300';
  return (
    <tr className="border-b border-slate-800">
      <td className="py-1.5 px-2 text-slate-200">{q.qual_name}<div className="text-[10px] text-slate-500">{q.attr_key}</div></td>
      <td className="py-1.5 px-2 text-slate-300 text-xs">{q.required_values}</td>
      <td className="py-1.5 px-2 text-slate-300 text-xs">{q.member_value ?? <span className="text-slate-500 italic">missing</span>}</td>
      <td className={`py-1.5 px-2 text-xs font-medium ${cls}`}>{q.result.toUpperCase()}</td>
    </tr>
  );
}

function SurveyResultCard({ s }: { s: SimulatorSurveyResult }) {
  const [open, setOpen] = useState(false);
  const reasonCls = s.reason_excluded
    ? 'text-red-400'
    : s.quals_unknown.length > 0
      ? 'text-amber-400'
      : 'text-emerald-400';
  const reasonText = s.reason_excluded ?? s.reason_included ?? '';
  const totalQuals = s.quals_passed.length + s.quals_failed.length + s.quals_unknown.length;

  return (
    <div className="bg-slate-800 rounded-xl p-4 space-y-2" data-testid="simulator-result-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${
              s.exchange === 'ps' ? 'bg-indigo-900/50 text-indigo-300 border-indigo-700' : 'bg-fuchsia-900/50 text-fuchsia-300 border-fuchsia-700'
            }`}>{s.exchange}</span>
            <span className="text-xs text-slate-400">{s.survey_id}</span>
          </div>
          <p className="text-white font-medium truncate">{s.title}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
            <span>${s.cpi.toFixed(2)} CPI</span>
            <span>{s.loi}m LOI</span>
            <span>{s.ir}% IR</span>
          </div>
        </div>
      </div>
      <div className={`text-xs font-medium ${reasonCls}`}>{reasonText}</div>

      {totalQuals > 0 && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
        >
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Qual Details ({totalQuals})
        </button>
      )}

      {open && (
        <div className="bg-slate-900 rounded-lg p-2 mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-slate-500">
              <tr className="border-b border-slate-700">
                <th className="text-left py-1 px-2">Attribute</th>
                <th className="text-left py-1 px-2">Required</th>
                <th className="text-left py-1 px-2">Member</th>
                <th className="text-left py-1 px-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {s.quals_failed.map((q, i) => <QualRow key={`f${i}`} q={q} color="red" />)}
              {s.quals_unknown.map((q, i) => <QualRow key={`u${i}`} q={q} color="amber" />)}
              {s.quals_passed.map((q, i) => <QualRow key={`p${i}`} q={q} color="green" />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminSimulator() {
  const [profile, setProfile] = useState<SimulatorProfile>(DEFAULT_PROFILE);
  const [extraOpen, setExtraOpen] = useState(true);
  const [resultsTab, setResultsTab] = useState<'shown' | 'excluded'>('shown');

  const mutation = useMutation<SimulatorResult, Error, SimulatorProfile>({
    mutationFn: simulateTargeting,
  });

  const set = <K extends keyof SimulatorProfile>(key: K, value: SimulatorProfile[K]): void => {
    setProfile((p) => ({ ...p, [key]: value }));
  };

  const reset = (): void => {
    setProfile(DEFAULT_PROFILE);
    mutation.reset();
  };

  const submit = (): void => {
    mutation.mutate(profile);
  };

  const result = mutation.data;
  const visible = result ? (resultsTab === 'shown' ? result.shown : result.excluded) : [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-display text-white">Targeting Simulator</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Profile builder */}
        <div className="bg-slate-800 rounded-xl p-5 space-y-4">
          <div>
            <h2 className="text-sm font-bold text-white mb-1">Profile</h2>
            <p className="text-xs text-slate-400">Build a synthetic member profile to test against live inventory.</p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Age">
              <input type="number" min={18} max={99} value={profile.age}
                onChange={(e) => set('age', Number(e.target.value))} className={inputCls} />
            </Field>
            <Field label="Gender">
              <select value={profile.gender} onChange={(e) => set('gender', e.target.value as 'male' | 'female' | 'other')} className={inputCls}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Zip Code">
              <input type="text" maxLength={5} value={profile.zip_code}
                onChange={(e) => set('zip_code', e.target.value.replace(/\D/g, '').slice(0, 5))}
                className={inputCls} />
            </Field>
          </div>

          <button
            onClick={() => setExtraOpen((o) => !o)}
            className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
            data-testid="simulator-extra-toggle"
          >
            {extraOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Additional Demographics
          </button>

          {extraOpen && (
            <div className="space-y-3 border-t border-slate-700 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Annual HHI">
                  <select value={profile.hhi ?? ''} onChange={(e) => set('hhi', e.target.value ? Number(e.target.value) : undefined)} className={inputCls}>
                    <option value="">—</option>
                    {HHI_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
                <Field label="Education">
                  <select value={profile.education ?? ''} onChange={(e) => set('education', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {EDUCATION_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Employment">
                  <select value={profile.employment_status ?? ''} onChange={(e) => set('employment_status', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {EMPLOYMENT_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Race / Ethnicity">
                  <select value={profile.race_ethnicity ?? ''} onChange={(e) => set('race_ethnicity', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {RACE_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Language at Home">
                  <select value={profile.language_at_home ?? ''} onChange={(e) => set('language_at_home', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {LANGUAGE_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Pet Ownership">
                  <select value={profile.pet_ownership ?? ''} onChange={(e) => set('pet_ownership', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {PET_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Home Ownership">
                  <select value={profile.home_ownership ?? ''} onChange={(e) => set('home_ownership', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {HOME_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Auto Make">
                  <select value={profile.auto_make ?? ''} onChange={(e) => set('auto_make', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {AUTO_MAKE_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Political Party">
                  <select value={profile.political_party ?? ''} onChange={(e) => set('political_party', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {POLITICAL_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
                <Field label="Industry">
                  <select value={profile.industry ?? ''} onChange={(e) => set('industry', e.target.value || undefined)} className={inputCls}>
                    <option value="">—</option>
                    {INDUSTRY_OPTIONS.map((v) => <option key={v} value={v}>{humanLabel(v)}</option>)}
                  </select>
                </Field>
              </div>

              <div className="flex flex-wrap gap-4 text-xs text-slate-300">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!profile.hispanic_origin} onChange={(e) => set('hispanic_origin', e.target.checked)} /> Hispanic Origin</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!profile.has_children} onChange={(e) => set('has_children', e.target.checked)} /> Has Children</label>
                {profile.has_children && (
                  <label className="flex items-center gap-2">
                    Child Count
                    <input type="number" min={0} max={10} value={profile.child_count ?? 0}
                      onChange={(e) => set('child_count', Number(e.target.value))}
                      className="w-16 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white" />
                  </label>
                )}
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!profile.voter_registration} onChange={(e) => set('voter_registration', e.target.checked)} /> Voter Registered</label>
              </div>

              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Ailments</p>
                <MultiCheck value={profile.ailments ?? []} options={AILMENT_OPTIONS}
                  onChange={(v) => set('ailments', v.length > 0 ? v : undefined)} />
              </div>

              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Insurance Products</p>
                <MultiCheck value={profile.insurance_products ?? []} options={INSURANCE_OPTIONS}
                  onChange={(v) => set('insurance_products', v.length > 0 ? v : undefined)} />
              </div>

              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Social Media</p>
                <MultiCheck value={profile.social_media_use ?? []} options={SOCIAL_OPTIONS}
                  onChange={(v) => set('social_media_use', v.length > 0 ? v : undefined)} />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={submit}
              disabled={mutation.isPending}
              className="flex-1 py-2.5 rounded-lg bg-primary text-white font-medium text-sm flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-40"
              data-testid="run-simulation"
            >
              {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {mutation.isPending ? 'Running…' : 'Run Simulation'}
            </button>
            <button onClick={reset} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
        </div>

        {/* RIGHT: Results */}
        <div className="bg-slate-800 rounded-xl p-5 space-y-3 min-h-[400px]" data-testid="simulator-results">
          <h2 className="text-sm font-bold text-white">Results</h2>

          {mutation.isPending && (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Running simulation…
            </div>
          )}

          {mutation.isError && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-sm text-red-300">
              {mutation.error.message ?? 'Simulation failed'}
            </div>
          )}

          {result && (
            <>
              <div className="text-xs text-slate-300 bg-slate-900 rounded-lg p-3" data-testid="simulator-summary">
                Showing <strong className="text-white">{result.summary.total_shown}</strong> of{' '}
                <strong className="text-white">{result.summary.total_live}</strong> live surveys
                <span className="text-slate-500 mx-2">·</span>
                PS: {result.summary.ps_shown} shown / {result.summary.ps_excluded} excluded
                <span className="text-slate-500 mx-2">·</span>
                RFG: {result.summary.rfg_shown} shown / {result.summary.rfg_excluded} excluded
              </div>

              <div className="flex gap-1">
                <button
                  onClick={() => setResultsTab('shown')}
                  className={`text-xs px-3 py-1.5 rounded-full border ${resultsTab === 'shown' ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700' : 'bg-transparent text-slate-400 border-slate-700 hover:text-slate-200'}`}
                >Shown on Wall <span className="ml-1 text-[10px] tabular-nums">{result.summary.total_shown}</span></button>
                <button
                  onClick={() => setResultsTab('excluded')}
                  className={`text-xs px-3 py-1.5 rounded-full border ${resultsTab === 'excluded' ? 'bg-red-900/40 text-red-300 border-red-700' : 'bg-transparent text-slate-400 border-slate-700 hover:text-slate-200'}`}
                >Excluded <span className="ml-1 text-[10px] tabular-nums">{result.summary.total_excluded}</span></button>
              </div>

              {visible.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-8">
                  {resultsTab === 'excluded' ? 'No surveys excluded for this profile' : 'No surveys would appear on the wall'}
                </div>
              ) : (
                <div className="space-y-2 max-h-[700px] overflow-y-auto">
                  {visible.map((s) => <SurveyResultCard key={`${s.exchange}-${s.survey_id}`} s={s} />)}
                </div>
              )}
            </>
          )}

          {!result && !mutation.isPending && !mutation.isError && (
            <div className="text-center text-sm text-slate-500 py-8">
              Configure a profile and click Run Simulation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
